import { ALLOWED_MIME_TYPES, FILE_SIZE_LIMITS } from '../constants';
import { supabase, isSupabaseConfigured } from './supabaseClient';

export const PROFILE_AVATAR_BUCKET = 'profile-avatars';

function getProfileAvatarPath(userId: string): string {
  return `${userId}/avatar`;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function validateProfileAvatar(file: File): string | null {
  if (!ALLOWED_MIME_TYPES.IMAGE.includes(file.type)) {
    return 'Unsupported image type. Allowed: JPG, PNG, GIF, WebP, BMP, SVG.';
  }
  if (file.size > FILE_SIZE_LIMITS.IMAGE) {
    return `Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum 5 MB.`;
  }
  return null;
}

export function getProfileAvatarUrl(userId: string, updatedAt?: string): string | undefined {
  if (!isSupabaseConfigured()) return undefined;
  const { data } = supabase.storage.from(PROFILE_AVATAR_BUCKET).getPublicUrl(getProfileAvatarPath(userId));
  if (!data?.publicUrl) return undefined;
  return updatedAt ? `${data.publicUrl}?v=${encodeURIComponent(updatedAt)}` : data.publicUrl;
}

export async function uploadProfileAvatar(file: File, userId: string): Promise<string> {
  if (!isSupabaseConfigured()) {
    return fileToDataUrl(file);
  }

  const storagePath = getProfileAvatarPath(userId);
  const { error } = await supabase.storage
    .from(PROFILE_AVATAR_BUCKET)
    .upload(storagePath, file, { cacheControl: '3600', upsert: true, contentType: file.type });

  if (error) {
    throw new Error(error.message || 'Unable to upload profile photo.');
  }

  return getProfileAvatarUrl(userId, new Date().toISOString()) || '';
}

export async function removeProfileAvatar(userId: string): Promise<void> {
  if (!isSupabaseConfigured()) return;

  const { error } = await supabase.storage
    .from(PROFILE_AVATAR_BUCKET)
    .remove([getProfileAvatarPath(userId)]);

  if (error && !/not found/i.test(error.message || '')) {
    throw new Error(error.message || 'Unable to remove profile photo.');
  }
}
